"use client"

/**
 * Color-scheme (mode) row for the profile menu — icon-only segmented control.
 * Same job as Settings → Appearance theme, reachable without leaving the menu.
 */

import { useTheme, type ColorScheme } from "@exxatdesignux/ui/hooks/use-color-scheme"

import { ButtonSegmentedControl } from "@/components/ui/button-segmented-control"
import { Kbd, KbdGroup } from "@/components/ui/kbd"
import { useAppTheme } from "@/hooks/use-app-theme"
import { useAltKeyLabel, useModKeyLabel } from "@/hooks/use-mod-key-label"

const THEME_OPTIONS = [
  { value: "light", label: "Light", icon: "fa-light fa-sun" },
  { value: "dark", label: "Dark", icon: "fa-light fa-moon" },
  { value: "system", label: "System", icon: "fa-light fa-desktop" },
] as const

export function ThemeModeMenu() {
  const { theme, setTheme } = useTheme()
  const { mounted } = useAppTheme()
  const value = (mounted ? (theme ?? "system") : "system") as ColorScheme
  const mod = useModKeyLabel()
  const alt = useAltKeyLabel()

  return (
    <div
      role="group"
      aria-label="Color scheme"
      className="flex items-center justify-between gap-3 px-2 py-1.5"
      /* Keep the profile menu open while tapping segments. */
      onPointerDown={(event) => event.preventDefault()}
    >
      <span className="flex items-center gap-1.5 text-sm text-foreground">
        Mode
        <KbdGroup>
          <Kbd>{mod}</Kbd>
          <Kbd>{alt}</Kbd>
          <Kbd>D</Kbd>
        </KbdGroup>
      </span>
      <ButtonSegmentedControl
        aria-label="Color scheme"
        value={value}
        onValueChange={(next) => setTheme(next)}
        iconOnly
        tooltipSide="left"
        options={THEME_OPTIONS}
        className="shrink-0"
      />
    </div>
  )
}
